import type { JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoginScreen, type LoginResult, type LoginScreenEndpoints } from '@cca/ui';

// The Parish Portal adopts the estate's ONE sign-in screen (ADR-018 / AUTH-8):
// the shared @cca/ui <LoginScreen> replaces the bespoke LoginPage + VerifyPage +
// AuthLayout clones. Gold #86754D is the Parish Portal's signature colour (UX-1)
// — it drives the whole screen, and it is the SAME gold the worker's login-code
// email carries (renderOtpCodeEmail's default accent), so screen and email are
// two faces of one door. Cookie session model: the worker sets an HttpOnly
// `parishes_session` cookie, so onSignedIn persists nothing auth-bearing — it
// just keeps the display hint the old VerifyPage kept (auth_email) and enters the
// app. The design-system classes (@cca/ui/components) + tokens (@cca/ui/tokens)
// are loaded globally in main.tsx, so nothing extra is imported here.

/** Parishes' same-origin worker paths. The passkey bases are UNCHANGED; OTP now
 *  mounts the shared createOtpSignInRoutes factory, so its two paths are
 *  /otp/request + /otp/verify (were /otp-request + /otp-verify). */
const PARISHES_ENDPOINTS: LoginScreenEndpoints = {
  discoverOptions: '/api/auth/passkeys/discover-options',
  discoverVerify: '/api/auth/passkeys/discover-verify',
  passkeyOptions: '/api/auth/passkeys/options',
  passkeyVerify: '/api/auth/passkeys/verify',
  otpRequest: '/api/auth/otp/request',
  otpVerify: '/api/auth/otp/verify',
};

/** The unauthenticated sign-in door. Mounted at /auth/login; the shared screen
 *  owns BOTH the email step and the code step internally, so parishes no longer
 *  needs a separate routed /auth/verify. */
export function ParishesSignIn(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/';

  const onSignedIn = (result: LoginResult): void => {
    // The session cookie is set by the worker (HttpOnly); keep only the display
    // hint the old VerifyPage persisted (auth_email — display only, never auth),
    // then enter the app.
    if (result.email) localStorage.setItem('auth_email', result.email);
    navigate(returnTo, { replace: true });
  };

  return (
    <LoginScreen
      portalName="Parish Portal"
      accentColor="#86754D"
      endpoints={PARISHES_ENDPOINTS}
      onSignedIn={onSignedIn}
      authzDeniedCopy="You do not have access to the Parish Portal. If you believe this is a mistake, contact the registrar at registrar@confessinganglicans.nz."
    />
  );
}
